/**
 * TradingModeToggle - Switch between DEX (Wallet) and CEX (Custodial) modes
 */

import { useState } from 'react';
import { useWallet, SUPPORTED_CHAINS, TradingMode } from '../contexts/WalletContext';
import { cn } from '../utils/cn';
import { 
  Wallet, 
  Building2, 
  ChevronDown, 
  Power, 
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Copy,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function TradingModeToggle() {
  const { 
    wallet, 
    tradingMode, 
    connect, 
    disconnect, 
    switchChain, 
    setTradingMode,
    formatAddress,
    getChainName,
    isChainSupported,
  } = useWallet();
  
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChainSelector, setShowChainSelector] = useState(false);

  const handleCopyAddress = () => {
    if (wallet.address) {
      navigator.clipboard.writeText(wallet.address);
      toast.success('Address copied to clipboard');
    }
  };

  const handleModeChange = (mode: TradingMode) => {
    if (mode === 'WALLET' && !wallet.isConnected) {
      connect();
    } else {
      setTradingMode(mode);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Mode Toggle */}
      <div className="flex items-center bg-white/[0.04] rounded-lg p-0.5">
        <button
          onClick={() => handleModeChange('CUSTODIAL')}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
            tradingMode === 'CUSTODIAL' 
              ? "bg-[#00d4aa] text-black" 
              : "text-[#8b949e] hover:text-white"
          )}
        >
          <Building2 className="h-4 w-4" />
          <span>Exchange</span>
        </button>
        <button
          onClick={() => handleModeChange('WALLET')}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
            tradingMode === 'WALLET' 
              ? "bg-[#00d4aa] text-black" 
              : "text-[#8b949e] hover:text-white"
          )}
        >
          <Wallet className="h-4 w-4" />
          <span>Wallet</span>
        </button>
      </div>

      {/* Wallet Status / Connect Button */}
      {tradingMode === 'WALLET' ? (
        wallet.isConnected ? (
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] rounded-lg hover:bg-white/[0.08] transition-colors"
            >
              {/* Connection Status Indicator */}
              <div className={cn(
                "h-2 w-2 rounded-full",
                wallet.chainId && isChainSupported(wallet.chainId) ? "bg-[#10b981]" : "bg-[#f59e0b]"
              )} />
              
              {/* Address */}
              <span className="font-mono text-sm text-white">
                {wallet.address && formatAddress(wallet.address)}
              </span>
              
              {/* Chain Badge */}
              {wallet.chainId && (
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-xs font-medium",
                  isChainSupported(wallet.chainId) 
                    ? "bg-[#00d4aa]/20 text-[#00d4aa]" 
                    : "bg-[#f59e0b]/20 text-[#f59e0b]"
                )}>
                  {getChainName(wallet.chainId)}
                </span>
              )}
              
              <ChevronDown className={cn(
                "h-4 w-4 text-[#8b949e] transition-transform",
                showDropdown && "rotate-180"
              )} />
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-[#161b22] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden animate-scale-in">
                {/* Wallet Info */}
                <div className="p-4 border-b border-white/[0.08]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#8b949e]">Connected Wallet</span>
                    <div className="flex items-center gap-1 text-[#10b981] text-xs">
                      <CheckCircle className="h-3 w-3" />
                      <span>Active</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-white">
                      {wallet.address && formatAddress(wallet.address)}
                    </span>
                    <button
                      onClick={handleCopyAddress}
                      className="p-1 hover:bg-white/[0.08] rounded transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5 text-[#8b949e]" />
                    </button>
                    <a
                      href={`https://etherscan.io/address/${wallet.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-white/[0.08] rounded transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-[#8b949e]" />
                    </a>
                  </div>
                  
                  {wallet.balance && (
                    <div className="mt-2 text-lg font-bold text-white">
                      {wallet.balance} {wallet.chainId && SUPPORTED_CHAINS[wallet.chainId as keyof typeof SUPPORTED_CHAINS]?.symbol || 'ETH'}
                    </div>
                  )}
                </div>

                {/* Chain Selector */}
                <div className="p-2">
                  <button
                    onClick={() => setShowChainSelector(!showChainSelector)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.04] rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-[#8b949e]" />
                      <span className="text-sm text-white">Switch Network</span>
                    </div>
                    <ChevronDown className={cn(
                      "h-4 w-4 text-[#8b949e] transition-transform",
                      showChainSelector && "rotate-180"
                    )} />
                  </button>
                  
                  {showChainSelector && (
                    <div className="mt-2 ml-2 space-y-1">
                      {Object.entries(SUPPORTED_CHAINS).map(([id, chain]) => (
                        <button
                          key={id}
                          onClick={() => {
                            switchChain(parseInt(id));
                            setShowChainSelector(false);
                          }}
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors",
                            wallet.chainId === parseInt(id)
                              ? "bg-[#00d4aa]/10 text-[#00d4aa]"
                              : "hover:bg-white/[0.04] text-white"
                          )}
                        >
                          <span className="text-sm">{chain.name}</span>
                          {wallet.chainId === parseInt(id) && (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Disconnect */}
                <div className="p-2 border-t border-white/[0.08]">
                  <button
                    onClick={() => {
                      disconnect();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[#f6465d] hover:bg-[#f6465d]/10 rounded-lg transition-colors"
                  >
                    <Power className="h-4 w-4" />
                    <span className="text-sm">Disconnect</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={connect}
            disabled={wallet.isConnecting}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all",
              "bg-gradient-to-r from-[#00d4aa] to-[#00b894] text-black",
              "hover:shadow-lg hover:shadow-[#00d4aa]/20",
              wallet.isConnecting && "opacity-50 cursor-wait"
            )}
          >
            {wallet.isConnecting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <Wallet className="h-4 w-4" />
                <span>Connect Wallet</span>
              </>
            )}
          </button>
        )
      ) : (
        /* CEX Mode - Show Balance Info */
        <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] rounded-lg">
          <CheckCircle className="h-4 w-4 text-[#10b981]" />
          <span className="text-sm text-white">Platform Account</span>
        </div>
      )}

      {/* Error Message */}
      {wallet.error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#f6465d]/10 rounded-lg">
          <AlertCircle className="h-4 w-4 text-[#f6465d]" />
          <span className="text-sm text-[#f6465d]">{wallet.error}</span>
        </div>
      )}
    </div>
  );
}
